import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useEffect, useRef } from "react";
import {
  AUDIENCE_PILLS,
  POPULAR_TOPIC_LABELS,
  POPULAR_TOPIC_SLUGS,
  type CanonicalAudience,
  type PopularTopicSlug,
} from "@/lib/blog/blogFilters";

interface Props {
  query: string;
  onQueryChange: (q: string) => void;
  audience: CanonicalAudience | "all";
  onAudienceChange: (a: CanonicalAudience | "all") => void;
  popularTopic: PopularTopicSlug | null;
  onPopularTopicChange: (slug: PopularTopicSlug | null) => void;
}

export function BlogSearchAndFilters({
  query,
  onQueryChange,
  audience,
  onAudienceChange,
  popularTopic,
  onPopularTopicChange,
}: Props) {
  const railRef = useRef<HTMLDivElement | null>(null);

  // Keep the active audience pill in view on load and when audience changes
  // (e.g. when arriving from a /blog/?audience=... deep link).
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const active = rail.querySelector<HTMLElement>('[data-active="true"]');
    if (active) {
      active.scrollIntoView({ inline: "center", block: "nearest" });
    }
  }, [audience]);

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search articles…"
          aria-label="Search articles"
          className="h-12 pl-12 text-base rounded-xl"
        />
      </div>
      <div className="relative -mx-4 sm:mx-0">
        <div
          ref={railRef}
          role="tablist"
          aria-label="Filter by audience"
          className="flex flex-nowrap gap-2 overflow-x-auto scrollbar-hide px-4 sm:px-0 pb-1 overscroll-x-contain touch-pan-x"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {AUDIENCE_PILLS.map((p) => {
            const active = p.value === audience;
            return (
              <button
                key={p.value}
                role="tab"
                data-active={active ? "true" : undefined}
                data-testid={`audience-pill-${p.value}`}
                aria-selected={active}
                onClick={() => onAudienceChange(p.value)}
                className={
                  "shrink-0 whitespace-nowrap h-9 px-3.5 rounded-full text-sm border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
                  (active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-border hover:bg-muted")
                }
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/*
        Popular-topic pills shown only on mobile/tablet where the sidebar
        (which hosts the desktop Popular Topics block) is not visible. The
        breakpoint mirrors `lg:` used by the index grid in BlogIndexPage.
      */}
      <div className="lg:hidden relative -mx-4 sm:mx-0">
        <div
          role="tablist"
          aria-label="Filter by popular topic"
          className="flex flex-nowrap gap-2 overflow-x-auto scrollbar-hide px-4 sm:px-0 pb-1 overscroll-x-contain touch-pan-x"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {[null, ...POPULAR_TOPIC_SLUGS].map((slug) => {
            const active = slug === popularTopic;
            const label = slug === null ? "All topics" : POPULAR_TOPIC_LABELS[slug];
            const key = slug ?? "__all__";
            return (
              <button
                key={key}
                role="tab"
                data-active={active ? "true" : undefined}
                data-testid={`topic-pill-${slug ?? "all"}`}
                aria-selected={active}
                onClick={() => onPopularTopicChange(slug)}
                className={
                  "shrink-0 whitespace-nowrap h-8 px-3 rounded-full text-xs border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
                  (active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-border hover:bg-muted")
                }
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}